import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { MapPin, Clock, Star, Store, Eye, Settings, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ProductCard } from '../../components/shopper/ProductCard';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { useBoutique } from '../../hooks/useBoutiques';
import { useProducts } from '../../hooks/useProducts';
import { useCartContext } from '../../hooks/useCart';
import { useFavoritesContext } from '../../hooks/useFavorites';
import { useAuth } from '../../hooks/useAuth';
import { BackButton } from '../../components/ui/BackButton';
import { BUSINESS_HOME, SHOPPER_HOME } from '../../constants/routes';
import { formatTime12h, isCurrentlyOpen } from '../../utils/time';
import { logAnalyticsEvent } from '../../services/analyticsLogger';
import type { DayOfWeek } from '../../types/business';

const WEEK_DAYS: { key: DayOfWeek; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

function formatHours(
  hours: string | Record<string, { open: string; close: string } | null>,
  closedNow?: boolean
): string {
  if (closedNow) {
    return 'Closed right now';
  }

  if (typeof hours === 'string') {
    return hours;
  }

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const today = days[new Date().getDay()];
  const todayHours = hours[today];

  if (!todayHours) {
    return 'Closed today';
  }

  if (isCurrentlyOpen(todayHours.open, todayHours.close)) {
    return `Open until ${formatTime12h(todayHours.close)}`;
  }

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const [oh, om] = todayHours.open.split(':').map(Number);
  const openMinutes = oh * 60 + om;

  if (currentMinutes < openMinutes) {
    return `Opens at ${formatTime12h(todayHours.open)}`;
  }

  return 'Closed';
}

function CoverImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Store className="h-16 w-16 text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function LogoImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <Store className="h-8 w-8 text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

export function BoutiquePage() {
  const { boutiqueId } = useParams<{ boutiqueId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const cart = useCartContext();
  const favorites = useFavoritesContext();
  
  const { boutique, loading } = useBoutique(boutiqueId || null);
  const { products, loading: productsLoading } = useProducts({ brandId: boutiqueId });

  const [showHoursPanel, setShowHoursPanel] = useState(false);
  const isOwnerPreview = !!(user && boutiqueId && user.uid === boutiqueId);

  const sessionStart = useRef<number>(Date.now());

  useEffect(() => {
    if (boutiqueId && !isOwnerPreview) {
      logAnalyticsEvent('page_view', boutiqueId, { shopperId: user?.uid });
    }
  }, [boutiqueId, isOwnerPreview, user?.uid]);

  useEffect(() => {
    if (!boutiqueId || isOwnerPreview) return;
    sessionStart.current = Date.now();

    return () => {
      const durationMs = Date.now() - sessionStart.current;
      if (durationMs > 2000) {
        logAnalyticsEvent('session_end', boutiqueId, {
          shopperId: user?.uid,
          metadata: { durationMs },
        });
      }
    };
  }, [boutiqueId, isOwnerPreview, user?.uid]);

  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`, { state: { from: location.pathname } });
  };

  const handleAddToCart = (productId: string, selectedSize?: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const size = product.sizes?.find((item) => item.label === selectedSize && item.available);
    if (!size) {
      navigate(`/product/${product.id}`);
      return;
    }

    logAnalyticsEvent('add_to_bag', product.brandId, { productId: product.id, shopperId: user?.uid });
    cart.addItem({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      businessId: product.brandId,
      price: product.price,
      size: size.label,
      quantity: 1,
      imageUrl: product.images?.[0] || '',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-pulse">
            <div className="w-16 h-16 rounded-full bg-purple-100"></div>
          </div>
          <p className="mt-4 text-body">Loading boutique...</p>
        </div>
      </div>
    );
  }

  if (!boutique) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif text-heading mb-4">Boutique not found</h2>
          <Button onClick={() => navigate(SHOPPER_HOME)}>Return Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <BackButton fallback={isOwnerPreview ? BUSINESS_HOME : SHOPPER_HOME} />
            <h1 className="text-xl font-serif text-heading">{boutique.name}</h1>
            <ShopperNavActions className="p-2 hover:bg-gray-100 rounded-full transition-colors" iconClassName="h-6 w-6 text-heading" />
          </div>
        </div>
      </header>

      {/* Owner Preview Banner */}
      {isOwnerPreview && (
        <div className="bg-primary text-white px-4 py-2.5 flex items-center justify-between z-30">
          <div className="flex items-center gap-2 text-sm">
            <Eye className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">You're viewing your store as shoppers see it</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/business/settings')}
              className="text-white hover:bg-white/20 text-xs h-7"
            >
              <Settings className="h-3.5 w-3.5 mr-1" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/business')}
              className="text-white hover:bg-white/20 text-xs h-7"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      )}

      {/* Cover Image */}
      <div className="relative h-64 md:h-80 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
        {(boutique.coverImage || boutique.logo) ? (
          <CoverImage src={boutique.coverImage || boutique.logo} alt={boutique.name} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Store className="h-16 w-16 text-gray-400" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      {/* Boutique Info */}
      <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-start gap-4">
            {/* Logo */}
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-200">
              {(boutique.logo || boutique.coverImage) ? (
                <LogoImage src={boutique.logo || boutique.coverImage} alt={boutique.name} />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Store className="h-8 w-8 text-gray-400" />
                </div>
              )}
            </div>
            
            {/* Info - stacked vertically */}
            <div className="flex-1">
              <h2 className="text-2xl font-serif text-heading mb-2">{boutique.name}</h2>
              
              {/* Rating */}
              <div className="flex items-center gap-1 text-sm text-body mb-1">
                <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                <span>{boutique.rating} ({boutique.reviewCount} reviews)</span>
              </div>
              
              {/* Address */}
              <div className="flex items-start gap-1 text-sm text-body mb-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  {boutique.formattedAddress || (typeof boutique.address === 'string' ? boutique.address : boutique.address ? `${boutique.address.street}, ${boutique.address.city}, ${boutique.address.state}` : '')}
                </span>
              </div>
              
              {/* Hours */}
              <div className={`text-sm mb-1 ${boutique.closedNow ? 'text-red-500' : 'text-green-600'}`}>
                {formatHours(boutique.hours, boutique.closedNow)}
              </div>
              
              {/* Store Hours link */}
              <button
                onClick={() => setShowHoursPanel(true)}
                className="text-sm text-body underline hover:text-heading"
              >
                View All Hours
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <h3 className="text-xl font-serif text-heading mb-6">Shop Collection</h3>
        
        {productsLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-gray-100 rounded-lg" />
                <div className="mt-2 h-3 bg-gray-100 rounded w-3/4" />
                <div className="mt-1 h-3 bg-gray-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-body">No products available at this time.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div 
                key={product.id} 
                onClick={() => handleProductClick(product.id)}
                className="cursor-pointer"
              >
                <ProductCard
                  product={{
                    id: product.id,
                    name: product.name,
                    brand: product.brand,
                    price: product.price,
                    compareAtPrice: product.compareAtPrice,
                    imageUrl: product.images?.[0] || '',
                    sizes: product.sizes,
                  }}
                  onAddToCart={(size, e) => {
                    e?.stopPropagation?.();
                    handleAddToCart(product.id, size);
                  }}
                  onFavorite={() => favorites.toggleFavorite({
                    productId: product.id,
                    name: product.name,
                    brand: product.brand,
                    price: product.price,
                    imageUrl: product.images?.[0] || '',
                  })}
                  isFavorited={favorites.isFavorited(product.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Store Hours Panel */}
      {showHoursPanel && boutique.hours && typeof boutique.hours !== 'string' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowHoursPanel(false)}
          />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <Clock className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-serif font-semibold text-heading">Store Hours</h3>
              </div>
              <button
                onClick={() => setShowHoursPanel(false)}
                className="p-2 -mr-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-2">
              {WEEK_DAYS.map(({ key, label }) => {
                const dayHours = (boutique.hours as Record<string, { open: string; close: string } | null>)[key];
                const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const isToday = days[new Date().getDay()] === key;

                return (
                  <div
                    key={key}
                    className={`flex items-center justify-between py-2.5 px-3 rounded-xl ${
                      isToday ? 'bg-primary/5 border border-primary/15' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isToday ? 'text-primary' : 'text-heading'}`}>
                        {label}
                      </span>
                      {isToday && (
                        <span className="text-[10px] font-semibold uppercase text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                          Today
                        </span>
                      )}
                    </div>
                    <span className={`text-sm ${dayHours ? 'text-heading' : 'text-gray-400'}`}>
                      {dayHours
                        ? `${formatTime12h(dayHours.open)} – ${formatTime12h(dayHours.close)}`
                        : 'Closed'}
                    </span>
                  </div>
                );
              })}
            </div>
            {boutique.closedNow && (
              <div className="mx-5 mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 font-medium">
                  This store is temporarily closed
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


